import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    OnDestroy,
    OnInit,
} from '@angular/core';
import { Router } from '@angular/router';
import {
    AppStateFacade,
    Setting,
} from '@app-state';
import { PageMetadataRegistry } from '@app/router';
import { CHART_ENABLE_ANIMATIONS } from '@components/ui-chart/base-chart';
import {
    ChartLineStyle,
    ChartTooltipTrigger,
} from '@components/ui-chart/base-chart.types';
import { IPieChartData } from '@components/ui-chart/pie/ui-pie-chart.types';
import {
    enumMapper,
    FolderType,
    ICountStats,
    Interval,
    JobState,
    ROBOT_STATE_FILTER,
    RobotState,
} from '@models';
import { TranslateService } from '@ngx-translate/core';
import { encodeFilters } from '@pages/generics/grid';
import {
    DEFAULT_TRANSACTION_INTERVALS,
    intervalToUnit,
    unitToFormat,
} from '@pages/shared/chart.config';
import {
    ChartColorService,
    ChartService,
    ResourceMapperService,
    SignalRService,
    StatsService,
    StorageService,
    TransactionService,
} from '@services';
import { Logger } from '@services/logger/abstract.logger';
import { IDropdownOption } from '@uipath/angular/components/ui-grid';
import { unsubscribe } from '@utils/object.utils';

import cloneDeep from 'lodash-es/cloneDeep';
import values from 'lodash-es/values';
import * as moment from 'moment';
import {
    BehaviorSubject,
    combineLatest,
    iif,
    interval,
    merge,
    Observable,
    of,
    Subject,
    Subscription,
} from 'rxjs';
import {
    catchError,
    debounceTime,
    delay,
    filter,
    finalize,
    map,
    retryWhen,
    switchMap,
    switchMapTo,
    takeUntil,
    tap,
    throttleTime,
} from 'rxjs/operators';

interface IStatsPieChart {
    loading$: BehaviorSubject<boolean>;
    hasAccess$: BehaviorSubject<boolean>;
    hasData$: BehaviorSubject<boolean>;
    data: IPieChartData[] | null;
    legend: string[] | null;
    colors: string[];
}

interface IStatsLineChart {
    loading$: BehaviorSubject<boolean>;
    hasAccess$: BehaviorSubject<boolean>;
    hasData$: BehaviorSubject<boolean>;
    data: number[] | null;
    legend: string[] | null;
    labels: string[] | null;
    colors: string[];
}

// FIXME: expose these from a common source
const ROBOT_STATES = enumMapper(RobotState, {
    resourceKey: 'RobotState',
    filterValueMap: ROBOT_STATE_FILTER,
});

// FIXME: expose these from a common source
const JOB_STATES = enumMapper(JobState, {
    resourceKey: 'JobState',
});

@Component({
    templateUrl: './stats.page.html',
    styleUrls: ['./stats.page.scss'],
    providers: [
        StatsService,
        TransactionService,
        ChartService,
        {
            provide: CHART_ENABLE_ANIMATIONS,
            useValue: true,
        },
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatsPageComponent implements OnInit, OnDestroy {
    public readonly ChartLineStyle = ChartLineStyle;
    public readonly ChartTooltipTrigger = ChartTooltipTrigger;
    public readonly defaultTransactionIntervals = DEFAULT_TRANSACTION_INTERVALS;

    public readonly FolderType = FolderType;
    public readonly folderType$ = this._appState.user.folder.type$;

    public countStatistics: ICountStats[] = [{
        title: 'Processes',
    }, {
        title: 'Assets',
    }, {
        title: 'Queues',
    }, {
        title: 'Triggers',
    }];
    public hiddenCountStatistics$!: Observable<string[]>;

    public transactionInterval$: BehaviorSubject<Interval>;
    public intervalLabel$!: Observable<string>;

    public sessionChart: IStatsPieChart = {
        loading$: new BehaviorSubject<boolean>(true),
        hasAccess$: new BehaviorSubject<boolean>(false),
        hasData$: new BehaviorSubject<boolean>(false),
        data: null,
        legend: null,
        colors: this._chartColorService.session,
    };

    public jobChart: IStatsPieChart = {
        loading$: new BehaviorSubject<boolean>(true),
        hasAccess$: new BehaviorSubject<boolean>(false),
        hasData$: new BehaviorSubject<boolean>(false),
        data: null,
        legend: null,
        colors: this._chartColorService.jobs,
    };

    public transactionChart: IStatsLineChart = {
        loading$: new BehaviorSubject<boolean>(true),
        hasAccess$: new BehaviorSubject<boolean>(false),
        hasData$: new BehaviorSubject<boolean>(false),
        data: null,
        legend: null,
        labels: null,
        colors: this._chartColorService.transactions,
    };

    public get labelFormat(): string {
        return this._labelFormat;
    }
    public set labelFormat(value: string) {
        this._labelFormat = unitToFormat(value);
    }

    private _labelFormat = '';
    private _dashboardCountSub?: Subscription;
    private _transactionSub?: Subscription;

    private _stateLabels = {
        sessions: values(ResourceMapperService.sessionStatus),
        jobs: values(ResourceMapperService.jobStatus),
    };
    private _intervalCacheKey: string;
    private _timeoutInterval = 15 * 1000;

    private _destroyed$ = new Subject();

    constructor(
        private _appState: AppStateFacade,
        private _pageRegistry: PageMetadataRegistry,
        private _statsService: StatsService,
        private _transactionService: TransactionService,
        private _chartService: ChartService,
        private _signalRService: SignalRService,
        private _translateService: TranslateService,
        private _router: Router,
        private _cd: ChangeDetectorRef,
        private _logger: Logger,
        private _storageService: StorageService,
        private _chartColorService: ChartColorService,
    ) {
        const userId = this._appState.user.value!.id;
        this._intervalCacheKey = `${this._router.url} chart - ${userId} - Transactions`;
        this.transactionInterval$ = new BehaviorSubject(this._storageService.get(this._intervalCacheKey, Interval.Day));

        this._appState.user.folder.change$
            .pipe(
                takeUntil(this._destroyed$),
            )
            .subscribe(() => {
                this._loadDashboardCount();
                this.loadTransactionChart(this.transactionInterval$.value);
            });

        this.hiddenCountStatistics$ = this._appState.user.folder.isClassic$
            .pipe(
                map(isClassic => isClassic
                    ? []
                    : ['Triggers'],
                ),
                takeUntil(this._destroyed$),
            );

        this.countStatistics.forEach((item: ICountStats) => {
            item.page = this._pageRegistry.findByTitle(item.title);
        });
    }

    ngOnInit() {
        this._initializePieChart$(
            merge(
                of(void 0),
                this._signalRService.sessions$,
                this._appState.user.folder.change$,
            ),
            'Robots.View',
            this.sessionChart,
            this._stateLabels.sessions,
            this._statsService.getSessionsStats(),
        ).pipe(
            takeUntil(this._destroyed$),
        ).subscribe(this._refreshSessionChart);

        this._initializePieChart$(
            merge(
                of(void 0),
                this._signalRService.jobs$,
                this._appState.user.folder.change$,
            ),
            'Jobs.View',
            this.jobChart,
            this._stateLabels.jobs,
            this._statsService.getJobsStats(),
        ).pipe(
            takeUntil(this._destroyed$),
        ).subscribe(this._refreshJobChart);

        this.intervalLabel$ = merge(
            this._appState.language.change$,
            this.transactionInterval$,
        ).pipe(
            map(_ => this._chartService.getTransactionsLabelForInterval(this.transactionInterval$.value)),
        );

        this._signalRService.watchAllJobs();
        this._signalRService.watchAllRobots();
        this._loadDashboardCount();
        this.loadTransactionChart(this.transactionInterval$.value);
    }

    ngOnDestroy() {
        unsubscribe(
            this._dashboardCountSub,
            this._transactionSub,
        );
        this._signalRService.watchAllJobs(false);
        this._signalRService.watchAllRobots(false);

        this._destroyed$.next();
    }

    public onIntervalChange(transactionInterval: Interval) {
        // if not filter is saved in localStorage, use the default
        if (transactionInterval !== null) {
            this.transactionInterval$.next(transactionInterval);
        }
        this.loadTransactionChart(this.transactionInterval$.value);
        this._storageService.set(this._intervalCacheKey, this.transactionInterval$.value);
    }

    public onLabelClick(type: string, translationKey: string | null) {
        let state: IDropdownOption;

        if (type === 'robots') {
            state = ROBOT_STATES.find(r => r.translationKey === translationKey)!;
        } else if (type === 'jobs') {
            state = JOB_STATES.find(j => j.translationKey === translationKey)!;
        } else { throw Error(`unkown type: ${type}`); }

        this._router.navigate([`/${type}`], {
            queryParams: encodeFilters({ state }),
        });
    }

    public loadTransactionChart(transactionInterval: Interval) {
        const transactionLabels = values(ResourceMapperService.transactionStatus);

        this.transactionChart.hasData$.next(false);
        this.transactionChart.loading$.next(true);

        this.labelFormat = intervalToUnit(transactionInterval);

        if (this._transactionSub) { this._transactionSub.unsubscribe(); }

        this._transactionSub =
            this._appState.user.permissions.isGranted('Queues.View&Transactions.View')
                .pipe(
                    tap((hasAccess) => {
                        if (!hasAccess) {
                            this.transactionChart.loading$.next(false);
                        }

                        this.transactionChart.hasAccess$.next(hasAccess);
                    }),
                    filter((hasAccess) => hasAccess),
                    switchMap(() =>
                        combineLatest([
                            iif(
                                () => this._appState.user.folder.hasAny,
                                this._transactionService
                                    .getTransactionStats(transactionInterval, 0),
                                of(null),
                            ).pipe(
                                catchError(() => of(null)),
                                finalize(() => this.transactionChart.loading$.next(false)),
                            ),
                            this._translateService
                                .stream(transactionLabels),
                        ]),
                    ),
                )
                .subscribe(([result, labelTranslations]) => {
                    if (!result) {
                        this._logger.error('An error has occurred while fetching the network response');
                        return;
                    }

                    let chartData = cloneDeep(result.chartData);

                    // Chart lines are rendered in the order that they are in this array. Reverse them so the totals line is rendered
                    // first, not last (i.e. so it no longer covers the other lines).
                    chartData.reverse();

                    const chartLabels = chartData
                        .map((entry: { label: string | number; }) => ResourceMapperService.transactionStatus[entry.label]);

                    this.transactionChart.legend = [];
                    this.transactionChart.data = [];

                    chartData = chartData.map((entry: { label: any; }, idx: number) => {
                        entry.label = labelTranslations[chartLabels[idx]];
                        this.transactionChart.legend![idx] = labelTranslations[chartLabels[idx]];
                        return entry;
                    });

                    chartData.forEach((dataset: { data: number; }) => {
                        this.transactionChart.data!.push(dataset.data);
                    });

                    this.transactionChart.labels = result
                        .chartLabels
                        .map((label: moment.MomentInput) => moment(label)
                            .tz(this._appState.settings.get(Setting.timezone)!)
                            .format(this.labelFormat));

                    this.transactionChart.hasData$.next(result.hasData);
                });
    }

    public getLargeScreenSize(disabledCount: number) {
        return Math.floor(100 / (this.countStatistics.length - disabledCount));
    }

    public getMediumScreenSize(disabledCount: number) {
        return (this.countStatistics.length - disabledCount) % 2 ? 33 : 50;
    }

    private _refreshSessionChart = ([counts, translations]: [ICountStats[], Record<string, string>]) => {
        const countData: number[] = [];
        const actualLabels: string[] = [];

        this.sessionChart.hasData$.next(false);

        counts.forEach(({ count = 0, title }) => {
            countData.push(count);
            actualLabels.push(ResourceMapperService.sessionStatus[title]);
            if (count > 0) {
                this.sessionChart.hasData$.next(true);
            }
        });

        this.sessionChart.data = countData.map((value, index) => ({
            key: actualLabels[index],
            value,
        }));
        this.sessionChart.legend = actualLabels.map(val => translations[val]);

        this.sessionChart.loading$.next(false);
    }

    private _refreshJobChart = ([counts, translations]: [ICountStats[], Record<string, string>]) => {
        const countData: number[] = [];
        const actualLabels: string[] = [];

        this.jobChart.hasData$.next(false);

        counts.forEach(({ count = 0, title }) => {
            countData.push(count);
            actualLabels.push(ResourceMapperService.jobStatus[title]);
            if (count > 0) {
                this.jobChart.hasData$.next(true);
            }
        });

        this.jobChart.data = countData.map((value, index) => ({
            key: actualLabels[index],
            value,
        }));
        this.jobChart.legend = actualLabels.map(val => translations[val]);

        this.jobChart.loading$.next(false);
    }

    private _loadDashboardCount() {
        if (this._dashboardCountSub) { this._dashboardCountSub.unsubscribe(); }

        this._dashboardCountSub =
            merge(
                this._appState.user.permissions.value$,
                interval(this._timeoutInterval),
            )
                .pipe(
                    switchMap(() => this._statsService.getCountStats()),
                    retryWhen(error => {
                        return delay(this._timeoutInterval)(error);
                    }),
                )
                .subscribe((counts: Array<ICountStats>) => {
                    (counts || []).forEach((count, idx) => {
                        count.page = this.countStatistics[idx].page;
                        this.countStatistics[idx] = count;
                    });
                    this._cd.markForCheck();
                });
    }

    private _initializePieChart$ = <T>(
        trigger$: Observable<any>,
        permission: string,
        chart: IStatsPieChart,
        labels: string[],
        fetch: Observable<T>,
    ) => {
        // only trigger if we have access, otherwise update the loading state
        const triggers = trigger$.pipe(
            switchMapTo(this._appState.user.permissions.isGranted(permission)),
            tap((hasAccess) => {
                if (!hasAccess) {
                    chart.loading$.next(false);
                }

                chart.hasAccess$.next(hasAccess);
            }),
            filter(Boolean),
        );

        // fetch at most 1 request / second
        const source =
            merge(
                triggers,
                triggers
                    .pipe(debounceTime(900)),
            ).pipe(
                throttleTime(1000),
                switchMapTo(fetch.pipe(catchError(() => of([])))),
            );

        return combineLatest([
            source,
            this._translateService.stream(labels),
        ]);
    }
}
